import React, { useState, useEffect } from 'react';
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Button from '@mui/material/Button';
import FeedIcon from '@mui/icons-material/Feed';

function Salegstreport() {
    const [dataBill, setDataBill] = useState([]);
    const [dataItemBtoB, setDataItemBtoB] = useState([]);
    const [dataItemBtoC, setDataItemBtoC] = useState([]);
    const [dataItemHSNBtoB, setDataItemHSNBtoB] = useState([]);
    const [dataItemHSNBtoC, setDataItemHSNBtoC] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, branchId, token, logout, permissions } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
    }, [isAuthenticated, token, logout]);

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };


    const fetchTableDataBTOB = async () => {
        try {
            const response = await fetchData('ReportApi', `/SaleGstBtoB/${fromDate}/${toDate}/${companyId}`, token);
            if (response.responseCode === '0000') {
                throw new Error(`Failed to fetch BTOB data`);
            }
            const data = response.response_data;
            setDataItemBtoB(data); // Assuming you are storing it in state
            return data;
        } catch (error) {
            console.error("Error fetching BTOB data:", error);
            throw error; // Propagate error to the caller
        }
    };

    const fetchTableDataBTOC = async () => {
        try {
            const response = await fetchData('ReportApi', `/SaleGstBtoC/${fromDate}/${toDate}/${companyId}`, token);
            if (response.responseCode === '0000') {
                throw new Error(`Failed to fetch BTOC data`);
            }
            const data = response.response_data;
            setDataItemBtoC(data); // Assuming you are storing it in state
            return data;
        } catch (error) {
            console.error("Error fetching BTOC data:", error);
            throw error; // Propagate error to the caller
        }
    };

    const fetchTableDataHSNBtoB = async () => {
        try {
            const response = await fetchData('ReportApi', `/SaleGstHSNBtoB/${fromDate}/${toDate}/${companyId}`, token);
            if (response.responseCode === '0000') {
                throw new Error(`Failed to fetch BTOC data`);
            }
            const data = response.response_data;
            setDataItemHSNBtoB(data); // Assuming you are storing it in state
            return data;
        } catch (error) {
            console.error("Error fetching HSN data:", error);
            throw error; // Propagate error to the caller
        }
    };

    const fetchTableDataHSNBtoC = async () => {
        try {
            const response = await fetchData('ReportApi', `/SaleGstHSNBtoC/${fromDate}/${toDate}/${companyId}`, token);
            if (response.responseCode === '0000') {
                throw new Error(`Failed to fetch BTOC data`);
            }
            const data = response.response_data;
            setDataItemHSNBtoC(data); // Assuming you are storing it in state
            return data;
        } catch (error) {
            console.error("Error fetching HSN data:", error);
            throw error; // Propagate error to the caller
        }
    };


    const handleFromDateChange = (e) => {
        setFromDate(e.target.value);
    };

    const handleToDateChange = (e) => {
        setToDate(e.target.value);
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    // Helper to process data in chunks to prevent UI blocking
    const processInChunks = async (data, processFunc, chunkSize = 100) => {
        for (let i = 0; i < data.length; i += chunkSize) {
            const chunk = data.slice(i, i + chunkSize);
            chunk.forEach((item, index) => processFunc(item, i + index));
            // Allow UI to breathe between chunks
            if (i + chunkSize < data.length) {
                await new Promise(resolve => setTimeout(resolve, 0));
            }
        }
    };


    const handleExportExcel = async () => {
        setIsLoading(true);

        // Allow UI to update before heavy processing
        await new Promise(resolve => setTimeout(resolve, 100));

        try {
            // Fetch all data (in parallel)
            const [btobData, btocData, hsnDataBtoB,hsnDataBtoC] = await Promise.all([
                fetchTableDataBTOB().catch(() => []),
                fetchTableDataBTOC().catch(() => []),
                fetchTableDataHSNBtoB().catch(() => []),
                fetchTableDataHSNBtoC().catch(() => []),
            ]);

            // Load Excel template with cache-busting (timestamp ensures fresh download)
            const cacheBuster = new Date().getTime();
            const response = await fetch(`/SaleGSTTemplate/SaleGSTExport.xlsx?v=${cacheBuster}`, {
                cache: 'no-store',
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0'
                }
            });
            const arrayBuffer = await response.arrayBuffer();
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(arrayBuffer);

            // ------------------ B2B SECTION ------------------
            const sheetNameB = "b2b,sez,de";
            const worksheetB = workbook.getWorksheet(sheetNameB);
            if (worksheetB && Array.isArray(btobData) && btobData.length > 0) {
                const startRowB = 5;
                const startColB = 1;
                let totalTaxableAmountB = 0;
                let totalTotalAmountB = 0;
                const processedBillNumbers = new Set(); // Track processed bill numbers

                await processInChunks(btobData, (item, index) => {
                    const rowNumberB = startRowB + index;
                    const row = worksheetB.getRow(rowNumberB);

                    row.getCell(startColB).value = item.GSTNumber ?? "";
                    row.getCell(startColB + 1).value = item.MasterName ?? "";
                    row.getCell(startColB + 2).value = item.BillNumber ?? "";
                    row.getCell(startColB + 3).value = item.BillDate ? formatDate(item.BillDate) : "";
                    
                    const totalAmountCell = row.getCell(startColB + 4);
                    totalAmountCell.value = Number(item.TotalAmount || 0);
                    totalAmountCell.numFmt = '0.00';
                    totalAmountCell.alignment = { horizontal: 'right' };
                    
                    row.getCell(startColB + 5).value = item.PlaceOfSupply ?? "";
                    row.getCell(startColB + 6).value = "No";
                    
                    const gstRateCell1 = row.getCell(startColB + 7);
                    gstRateCell1.value = Number(item.GSTRate || 0);
                    gstRateCell1.numFmt = '0.00';
                    gstRateCell1.alignment = { horizontal: 'right' };
                    
                    row.getCell(startColB + 8).value = "Regular";
                    row.getCell(startColB + 9).value = "";
                    
                    const gstRateCell2 = row.getCell(startColB + 10);
                    gstRateCell2.value = Number(item.GSTRate || 0);
                    gstRateCell2.numFmt = '0.00';
                    gstRateCell2.alignment = { horizontal: 'right' };
                    
                    const taxableAmountCell = row.getCell(startColB + 11);
                    taxableAmountCell.value = Number(item.TaxableAmount || 0);
                    taxableAmountCell.numFmt = '0.00';
                    taxableAmountCell.alignment = { horizontal: 'right' };

                    // Only add to total if this bill number hasn't been processed yet
                    if (!processedBillNumbers.has(item.BillNumber)) {
                        totalTotalAmountB += Number(item.TotalAmount || 0);
                        processedBillNumbers.add(item.BillNumber);
                    }

                    totalTaxableAmountB += Number(item.TaxableAmount || 0);
                    row.commit();
                });

                const totalRow = worksheetB.getRow(3);
                const totalCell1 = totalRow.getCell(startColB + 4);
                totalCell1.value = totalTotalAmountB;
                totalCell1.numFmt = '0.00';
                totalCell1.alignment = { horizontal: 'right' };
                totalCell1.font = { bold: true };
                
                const totalCell2 = totalRow.getCell(startColB + 11);
                totalCell2.value = totalTaxableAmountB;
                totalCell2.numFmt = '0.00';
                totalCell2.alignment = { horizontal: 'right' };
                totalCell2.font = { bold: true };
                totalRow.commit();
            }

            // ------------------ B2C SECTION ------------------
            const sheetNameC = "b2cs";
            const worksheetC = workbook.getWorksheet(sheetNameC);
            if (worksheetC && Array.isArray(btocData) && btocData.length > 0) {
                const startRowC = 5;
                const startColC = 2;
                let totalTaxableAmountC = 0;

                await processInChunks(btocData, (item, index) => {
                    const row = worksheetC.getRow(startRowC + index);
                    row.getCell(startColC).value = item.PlaceOfSupply ?? "";
                    
                    const gstRateCell1 = row.getCell(startColC + 1);
                    gstRateCell1.value = Number(item.GSTRate || 0);
                    gstRateCell1.numFmt = '0.00';
                    gstRateCell1.alignment = { horizontal: 'right' };
                    
                    const gstRateCell2 = row.getCell(startColC + 2);
                    gstRateCell2.value = Number(item.GSTRate || 0);
                    gstRateCell2.numFmt = '0.00';
                    gstRateCell2.alignment = { horizontal: 'right' };
                    
                    const taxableAmountCell = row.getCell(startColC + 3);
                    taxableAmountCell.value = Number(item.TaxableAmount || 0);
                    taxableAmountCell.numFmt = '0.00';
                    taxableAmountCell.alignment = { horizontal: 'right' };

                    totalTaxableAmountC += Number(item.TaxableAmount || 0);
                    row.commit();
                });

                const totalRow = worksheetC.getRow(3);
                const totalCell = totalRow.getCell(startColC + 3);
                totalCell.value = totalTaxableAmountC;
                totalCell.numFmt = '0.00';
                totalCell.alignment = { horizontal: 'right' };
                totalCell.font = { bold: true };
                totalRow.commit();
            }

            // ------------------ HSN BTOB SECTION ------------------
            const sheetNamehsn = "hsn(b2b)";
            const worksheethsn = workbook.getWorksheet(sheetNamehsn);
            if (worksheethsn && Array.isArray(hsnDataBtoB) && hsnDataBtoB.length > 0) {
                const startRowhsn = 5;
                const startColhsn = 1;
                let totals = { taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0 };

                await processInChunks(hsnDataBtoB, (item, index) => {
                    const row = worksheethsn.getRow(startRowhsn + index);
                    row.getCell(startColhsn).value = item.HSNCode ?? "";
                    row.getCell(startColhsn + 2).value = item.UQC ?? "";
                    
                    const qtyCell = row.getCell(startColhsn + 3);
                    qtyCell.value = Number(item.Quantity || 0);
                    qtyCell.numFmt = '0.00';
                    qtyCell.alignment = { horizontal: 'right' };
                    
                    const totalAmtCell = row.getCell(startColhsn + 4);
                    totalAmtCell.value = Number(item.TotalAmount || 0);
                    totalAmtCell.numFmt = '0.00';
                    totalAmtCell.alignment = { horizontal: 'right' };
                    
                    const gstRateCell = row.getCell(startColhsn + 5);
                    gstRateCell.value = Number(item.GSTRate || 0);
                    gstRateCell.numFmt = '0.00';
                    gstRateCell.alignment = { horizontal: 'right' };
                    
                    const taxableCell = row.getCell(startColhsn + 6);
                    taxableCell.value = Number(item.TaxableAmount || 0);
                    taxableCell.numFmt = '0.00';
                    taxableCell.alignment = { horizontal: 'right' };
                    
                    const cgstCell = row.getCell(startColhsn + 7);
                    cgstCell.value = Number(item.CGSTAmount || 0);
                    cgstCell.numFmt = '0.00';
                    cgstCell.alignment = { horizontal: 'right' };
                    
                    const sgstCell = row.getCell(startColhsn + 8);
                    sgstCell.value = Number(item.SGSTAmount || 0);
                    sgstCell.numFmt = '0.00';
                    sgstCell.alignment = { horizontal: 'right' };
                    
                    const igstCell = row.getCell(startColhsn + 9);
                    igstCell.value = Number(item.IGSTAmount || 0);
                    igstCell.numFmt = '0.00';
                    igstCell.alignment = { horizontal: 'right' };

                    totals.taxable += Number(item.TaxableAmount || 0);
                    totals.cgst += Number(item.CGSTAmount || 0);
                    totals.sgst += Number(item.SGSTAmount || 0);
                    totals.igst += Number(item.IGSTAmount || 0);
                    totals.total += Number(item.TotalAmount || 0);
                    row.commit();
                });

                const totalRow = worksheethsn.getRow(3);
                
                const totalCell1 = totalRow.getCell(startColhsn + 4);
                totalCell1.value = totals.total;
                totalCell1.numFmt = '0.00';
                totalCell1.alignment = { horizontal: 'right' };
                totalCell1.font = { bold: true };
                
                const totalCell2 = totalRow.getCell(startColhsn + 6);
                totalCell2.value = totals.taxable;
                totalCell2.numFmt = '0.00';
                totalCell2.alignment = { horizontal: 'right' };
                totalCell2.font = { bold: true };
                
                const totalCell3 = totalRow.getCell(startColhsn + 7);
                totalCell3.value = totals.cgst;
                totalCell3.numFmt = '0.00';
                totalCell3.alignment = { horizontal: 'right' };
                totalCell3.font = { bold: true };
                
                const totalCell4 = totalRow.getCell(startColhsn + 8);
                totalCell4.value = totals.sgst;
                totalCell4.numFmt = '0.00';
                totalCell4.alignment = { horizontal: 'right' };
                totalCell4.font = { bold: true };
                
                const totalCell5 = totalRow.getCell(startColhsn + 9);
                totalCell5.value = totals.igst;
                totalCell5.numFmt = '0.00';
                totalCell5.alignment = { horizontal: 'right' };
                totalCell5.font = { bold: true };
                
                totalRow.commit();
            }

            // ------------------ HSN BTOC SECTION ------------------
            const sheetNamehsnBtoC = "hsn(b2c)";
            const worksheethsnBtoC = workbook.getWorksheet(sheetNamehsnBtoC);
            if (worksheethsnBtoC && Array.isArray(hsnDataBtoC) && hsnDataBtoC.length > 0) {
                const startRowhsnBtoC = 5;
                const startColhsnBtoC = 1;
                let totalsBtoC = { taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0 };

                await processInChunks(hsnDataBtoC, (item, index) => {
                    const row = worksheethsnBtoC.getRow(startRowhsnBtoC + index);
                    row.getCell(startColhsnBtoC).value = item.HSNCode ?? "";
                    row.getCell(startColhsnBtoC + 2).value = item.UQC ?? "";
                    
                    const qtyCell = row.getCell(startColhsnBtoC + 3);
                    qtyCell.value = Number(item.Quantity || 0);
                    qtyCell.numFmt = '0.00';
                    qtyCell.alignment = { horizontal: 'right' };
                    
                    const totalAmtCell = row.getCell(startColhsnBtoC + 4);
                    totalAmtCell.value = Number(item.TotalAmount || 0);
                    totalAmtCell.numFmt = '0.00';
                    totalAmtCell.alignment = { horizontal: 'right' };
                    
                    const gstRateCell = row.getCell(startColhsnBtoC + 5);
                    gstRateCell.value = Number(item.GSTRate || 0);
                    gstRateCell.numFmt = '0.00';
                    gstRateCell.alignment = { horizontal: 'right' };
                    
                    const taxableCell = row.getCell(startColhsnBtoC + 6);
                    taxableCell.value = Number(item.TaxableAmount || 0);
                    taxableCell.numFmt = '0.00';
                    taxableCell.alignment = { horizontal: 'right' };
                    
                    const cgstCell = row.getCell(startColhsnBtoC + 7);
                    cgstCell.value = Number(item.CGSTAmount || 0);
                    cgstCell.numFmt = '0.00';
                    cgstCell.alignment = { horizontal: 'right' };
                    
                    const sgstCell = row.getCell(startColhsnBtoC + 8);
                    sgstCell.value = Number(item.SGSTAmount || 0);
                    sgstCell.numFmt = '0.00';
                    sgstCell.alignment = { horizontal: 'right' };
                    
                    const igstCell = row.getCell(startColhsnBtoC + 9);
                    igstCell.value = Number(item.IGSTAmount || 0);
                    igstCell.numFmt = '0.00';
                    igstCell.alignment = { horizontal: 'right' };

                    totalsBtoC.taxable += Number(item.TaxableAmount || 0);
                    totalsBtoC.cgst += Number(item.CGSTAmount || 0);
                    totalsBtoC.sgst += Number(item.SGSTAmount || 0);
                    totalsBtoC.igst += Number(item.IGSTAmount || 0);
                    totalsBtoC.total += Number(item.TotalAmount || 0);
                    row.commit();
                });

                const totalRowBtoC = worksheethsnBtoC.getRow(3);
                
                const totalCell1 = totalRowBtoC.getCell(startColhsnBtoC + 4);
                totalCell1.value = totalsBtoC.total;
                totalCell1.numFmt = '0.00';
                totalCell1.alignment = { horizontal: 'right' };
                totalCell1.font = { bold: true };
                
                const totalCell2 = totalRowBtoC.getCell(startColhsnBtoC + 6);
                totalCell2.value = totalsBtoC.taxable;
                totalCell2.numFmt = '0.00';
                totalCell2.alignment = { horizontal: 'right' };
                totalCell2.font = { bold: true };
                
                const totalCell3 = totalRowBtoC.getCell(startColhsnBtoC + 7);
                totalCell3.value = totalsBtoC.cgst;
                totalCell3.numFmt = '0.00';
                totalCell3.alignment = { horizontal: 'right' };
                totalCell3.font = { bold: true };
                
                const totalCell4 = totalRowBtoC.getCell(startColhsnBtoC + 8);
                totalCell4.value = totalsBtoC.sgst;
                totalCell4.numFmt = '0.00';
                totalCell4.alignment = { horizontal: 'right' };
                totalCell4.font = { bold: true };
                
                const totalCell5 = totalRowBtoC.getCell(startColhsnBtoC + 9);
                totalCell5.value = totalsBtoC.igst;
                totalCell5.numFmt = '0.00';
                totalCell5.alignment = { horizontal: 'right' };
                totalCell5.font = { bold: true };
                
                totalRowBtoC.commit();
            }

            // ------------------ SAVE EXCEL ------------------
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const monthYear = getMonthYear(toDate);
            saveAs(blob, `Sale GST(${monthYear}).xlsx`);
            alert("Excel file exported successfully");

            console.log("Excel file exported successfully");
        } catch (error) {
            console.error("Error exporting Excel file:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getMonthYear = (dateString) => {
        const date = new Date(dateString);
        const formatter = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });
        return formatter.format(date); // Returns "December 2024"
    };


    return (
        <div className="w-full mx-auto" style={{ width: '100%', marginTop: '10%' }}>
            <div style={{ padding: '20px', backgroundColor: '#DCE4C9', borderRadius: '8px', margin: 'auto', width: '30%', textAlign: 'center' }}>
                <h6
                    className="uppercase tracking-wide text-black font-serif flex-grow"
                    style={{
                        fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif',
                        color: 'black',
                        marginTop: '10px',
                        fontWeight: 'bold'
                    }}
                >
                    Sale GST Report
                </h6>

                <div className="w-full mb-2 flex">
                    <div style={{ flex: '2', maxWidth: '50%', paddingRight: '8px', marginBottom: '0', marginTop: '15px' }}>
                        <label style={{ display: 'block', color: 'Black', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }} htmlFor="fromDate">
                            From Date
                        </label>
                        <input
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                            type="date" value={fromDate} onChange={handleFromDateChange}
                            style={{ marginTop: '-10px', width: '100%' }}
                        />
                    </div>

                    <div style={{ flex: '2', maxWidth: '50%', marginLeft: '8px', marginBottom: '0', marginTop: '15px' }}>
                        <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                            To Date
                        </label>
                        <input
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                            type="date" value={toDate} onChange={handleToDateChange}
                            style={{ marginTop: '-10px', width: '100%' }}
                        />
                    </div>
                </div>

                <Button
                    style={{
                        background: 'linear-gradient(to right, #4D455D,#4D455D)',
                        color: 'black',
                        fontWeight: 'bolder',
                        textTransform: 'none',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                        maxHeight: '30px',
                        marginLeft: '15px',
                        marginTop: '1.5%'
                    }}
                    onClick={handleExportExcel}
                >
                    <FeedIcon style={{ color: 'white', fontWeight: 'bolder' }} />
                    <b style={{ color: 'white' }}> {isLoading ? "Processing..." : "Export to Excel"}</b>
                </Button>
                {/* {isLoading && <div className="loading-spinner">Loading...</div>} */}
            </div>
        </div>
    );
}

export default Salegstreport;
